import type React from 'react'
import { useState } from 'react'
import { type IResourceComponentsProps, useApiUrl, useNotification } from '@refinedev/core'
import { useTable, List, RefreshButton } from '@refinedev/antd'
import { Table, Tag, Popover, Drawer, Button, Space, Empty } from 'antd'

import {
  compareFn,
  convertSeconds,
  toSec,
  getPhoneUpdateColor,
  toData
} from '../../fn'
import { PhoneCommands } from '../../components/shared/PhoneCommands'
import { CustomSearchBar, filterData } from '../../components/shared/CustomSearchBar'

// Dopo quanti giorni senza un SMS la SIM va considerata sorda (allarme)
const SMS_ALARM_DAYS = 15
const ALIVE_S = 60

const smsAge = (record: any): number | null => (record?.smsLast ? toSec(record.smsLast) : null)

const smsState = (record: any): 'mai' | 'alarm' | 'ok' => {
  const age = smsAge(record)
  if (age === null) return 'mai'
  return age > SMS_ALARM_DAYS * 24 * 60 * 60 ? 'alarm' : 'ok'
}

const smsColor: Record<string, string> = { mai: '#888', alarm: 'purple', ok: 'green' }

const flowLabel = (record: any): string => {
  if (!record?.lastSeen) return '⚪ mai visto'
  const age = toSec(record.lastSeen)
  return age < ALIVE_S ? '🟢 vivo' : `🔴 muto da ${convertSeconds(age)}`
}

export const SmsList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, tableQuery } = useTable({
    syncWithLocation: true,

    pagination: {
      mode: 'off'
    }
  })

  const { open } = useNotification()
  const API_URL = useApiUrl()

  const [query, setQuery] = useState<any>()
  const [detail, setDetail] = useState<any>()

  // al primo render tableProps.dataSource è undefined: filterData lo restituisce tal quale
  const dataSource = (filterData(tableProps.dataSource, query) ?? []) as any[]
  const updatedTableProps = { ...tableProps, dataSource }

  const alive = dataSource.filter((r) => r.lastSeen && toSec(r.lastSeen) < ALIVE_S).length
  const muted = dataSource.length - alive
  const noSms = dataSource.filter((r) => smsState(r) === 'alarm').length

  return (
    <List
      headerButtons={
        <>
          <Space size='large' style={{ fontFamily: 'monospace' }}>
            <span>🟢 vive: <b>{alive}</b></span>
            <span>🔴 mute: <b>{muted}</b></span>
            <span style={{ color: noSms > 0 ? 'purple' : undefined }}>📨 senza SMS da {SMS_ALARM_DAYS}g: <b>{noSms}</b></span>
          </Space>
          <CustomSearchBar onFilter={setQuery} />
          <RefreshButton onClick={() => { void tableQuery.refetch?.() }} />
        </>
      }
      title='📨 SMS'
    >
      <Table
        {...updatedTableProps}
        rowKey='id'
      >
        <Table.Column
          dataIndex='id'
          title='📶 ID'
          sorter={{ compare: compareFn('id') }}
        />

        <Table.Column
          dataIndex='number'
          title='📞 Number'
          render={(value: any, record: any) => {
            const ph = record?.number
            return (
              <pre style={{ margin: 0 }}>
                {ph?.countryCode} {ph?.phone}{' '}
                <Tag color={ph?.source === 'ussd' ? 'green' : 'default'} style={{ fontSize: '10px' }}>
                  {ph?.source === 'ussd' ? 'SIM' : 'seed'}
                </Tag>
                <Popover
                  title='Numero letto dalla SIM (USSD *132#)'
                  content={
                    <span style={{ fontSize: '12px' }}>
                      {ph?.updated
                        ? `letto ${convertSeconds(toSec(ph.updated))} ago (${toData(ph.updated)})`
                        : 'mai letto: valore di partenza dal vault'}
                    </span>
                  }
                >
                  <div style={{ fontSize: '11px', color: ph?.updated ? 'green' : '#888' }}>
                    📲 {ph?.updated ? `${convertSeconds(toSec(ph.updated))} ago` : 'mai letto'}
                  </div>
                </Popover>
              </pre>
            )
          }}
          sorter={{ compare: compareFn('number', undefined, (a: any) => a?.number?.phone) }}
        />

        <Table.Column
          dataIndex='lastSeen'
          title='🟢 Inoltro'
          render={(value: any, record: any) => {
            const ageS = value ? toSec(value) : null
            const isAlive = ageS !== null && ageS < ALIVE_S
            return (
              <div style={{ fontSize: '12px' }}>
                <span style={{ color: isAlive ? 'green' : 'red', fontWeight: 'bold' }}>
                  {flowLabel(record)}
                </span>
                <div>
                  <Tag style={{ fontSize: '10px' }}>v{record?.flow?.version ?? '?'}</Tag>
                  <span style={{ fontSize: '10px', color: '#888' }}>automate-{record?.id}</span>
                </div>
              </div>
            )
          }}
          sorter={{ compare: compareFn('lastSeen') }}
        />

        <Table.Column
          dataIndex='credit'
          title='💶 Credit'
          render={(value: any, record: any) => (
            <Popover
              title='⏱ Credit Updated'
              content={
                <span style={{ fontSize: '12px' }}>
                  {record.creditUpdated
                    ? `${convertSeconds(toSec(record.creditUpdated))} ago (${toData(record.creditUpdated)})`
                    : 'mai aggiornato'}
                </span>
              }
            >
              <span style={{ color: record.creditUpdated ? getPhoneUpdateColor(toSec(record.creditUpdated) / 24 / 60 / 60) : '#888' }}>
                💶 {value ?? '—'}
                <div style={{ fontSize: '11px', color: '#888' }}>
                  {record.creditUpdated ? `${convertSeconds(toSec(record.creditUpdated))} ago` : 'mai aggiornato'}
                </div>
              </span>
            </Popover>
          )}
          sorter={{ compare: compareFn('credit') }}
        />

        <Table.Column
          dataIndex='smsLast'
          title='📨 Ultimi SMS'
          render={(value: any, record: any) => {
            const items = record?.sms?.items ?? []
            const last = items[0]
            const state = smsState(record)
            const body = String(last?.text ?? '')
            const preview = body.length > 90
              ? `${body.slice(0, 90)}…`
              : body
            return (
              <div style={{ fontSize: '12px', maxWidth: '360px' }}>
                {Boolean(last) && (
                  <Button type='link' style={{ padding: 0, height: 'auto', textAlign: 'left', whiteSpace: 'normal' }} onClick={() => { setDetail(record) }}>
                    <span style={{ color: '#000' }}>
                      <b>{convertSeconds(toSec(last.at))} ago</b> · {last.sender}: {preview}
                    </span>
                  </Button>
                )}
                {!last && <span style={{ color: '#888' }}>nessun messaggio in cronologia</span>}
                <div style={{ fontSize: '11px', color: '#888' }}>
                  <span style={{ color: smsColor[state], fontWeight: 'bold' }}>
                    {state === 'mai' && '⚪ mai ricevuto'}
                    {state === 'alarm' && `🟣 nessun SMS da ${convertSeconds(smsAge(record) as number)}`}
                    {state === 'ok' && `📨 ultimo ${convertSeconds(smsAge(record) as number)} ago`}
                  </span>
                  {' · '}1h: <b>{record?.sms?.last1h ?? 0}</b> · 24h: <b>{record?.sms?.last24h ?? 0}</b>
                  {items.length > 1 && (
                    <> · <Button type='link' size='small' style={{ padding: 0 }} onClick={() => { setDetail(record) }}>vedi {items.length}</Button></>
                  )}
                </div>
              </div>
            )
          }}
          sorter={{ compare: (a: any, b: any) => (a?.smsLast ?? 0) - (b?.smsLast ?? 0) }}
        />

        <Table.Column
          dataIndex='id'
          title='🕹 Comandi'
          render={(id: string, record: any) => (
            <PhoneCommands
              record={record}
              apiUrl={API_URL}
              open={open}
              showLabels={false}
              onAfterCommand={(key: string) => {
                // credito e numero arrivano dalla SIM in pochi secondi: rileggo per mostrarli
                if (key === 'credit' || key === 'number') setTimeout(() => { void tableQuery.refetch?.() }, 15000)
              }}
            />
          )}
        />
      </Table>

      <Drawer
        title={`📨 Ultimi messaggi · automate-${String(detail?.id ?? '')} ${String(detail?.number?.countryCode ?? '')} ${String(detail?.number?.phone ?? '')}`}
        open={Boolean(detail)}
        onClose={() => { setDetail(undefined) }}
        width={640}
      >
        {detail?.sms?.items?.length
          ? detail.sms.items.map((item: any, index: number) => (
            <div key={index} style={{ borderBottom: '1px solid #eee', padding: '8px 0' }}>
              <div style={{ fontSize: '11px', color: '#888' }}>
                ⏱ {toData(item.at)} · {item.sender} {item.otp && <Tag color='blue' style={{ fontSize: '10px' }}>codice</Tag>}
              </div>
              <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{item.text}</div>
            </div>
          ))
          : <Empty description='nessun messaggio in cronologia' />}
      </Drawer>
    </List>
  )
}
